import React, { useEffect, useMemo, useState } from 'react';
import { Skeleton, Slider } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import {
  updateModalSideFilters,
  updateSiteFilter,
} from '../../../../modules/SearchDatePicker/searchDatePickerSlice';
import { NumericRangeFilter } from '../../interface';
import './sideFilterNumericRange.less';
import useGetAttributeTranslation from '../../hooks/useGetAttributeTranslation';

interface Props {
  filter: NumericRangeFilter;
  pricingMaxLimitAndPlusFilter?: number;
  withModalScopeParams?: boolean;
  isLoading?: boolean;
  additionalPrice?: number;
  showPricePerPax?: boolean;
  localSideFilters?: Record<string, any> | null;
  setLocalSideFilters?: (f: Record<string, any>) => void;
}

const SideFilterNumericRange = ({
  filter,
  pricingMaxLimitAndPlusFilter,
  withModalScopeParams,
  isLoading,
  additionalPrice = 0,
  showPricePerPax = false,
  localSideFilters,
  setLocalSideFilters
}: Props) => {
  const dispatch = useDispatch();
  const { sideFilters: reduxSideFilters, modalSideFilters } = useSelector(
    (state: RootState) => {
      return state.search;
    }
  );
  const sideFilters = useMemo(
    () => (localSideFilters? localSideFilters : withModalScopeParams ? modalSideFilters : reduxSideFilters),
    [withModalScopeParams, modalSideFilters, reduxSideFilters, localSideFilters]
  );

  function getPaxObj() {
    const params = new URL(window.location.href).searchParams;
    const { adult, child, multi_room_guests, ...rest } =
      Object.fromEntries(params);
    const multiRoomGuests =
      typeof multi_room_guests === 'string'
        ? JSON.parse(multi_room_guests)
        : [];

    let paxObj = {
      adult: 0,
      child: 0,
    };
    if (Array.isArray(multiRoomGuests) && multiRoomGuests?.length > 0) {
      paxObj = multiRoomGuests.reduce(
        (acc, curr) => {
          acc.adult += +curr.adult;
          acc.child += +curr.child;
          return acc;
        },
        {
          adult: 0,
          child: 0,
        }
      );
    } else {
      paxObj = {
        adult: +(adult || 0),
        child: +(child || 0),
      };
    }
    return paxObj;
  }

  const paxObj = getPaxObj();

  // BE marks price filters with price_mode when their basis is verified:
  // 'per_person' (e.g. packages) — min/max are already per-person;
  // 'total' (e.g. hotels) — min/max are a room/stay total matching the cards.
  // In both cases the range must be rendered as-is, matching what the result
  // cards display. Only when the flag is ABSENT do we keep the legacy
  // divide-by-pax behavior.
  const hasExplicitPriceMode = !!filter.price_mode;

  const totalPax =
    !hasExplicitPriceMode &&
    showPricePerPax &&
    paxObj?.adult + paxObj?.child > 0
      ? paxObj?.adult + paxObj?.child
      : 1;
  const filterMin = !pricingMaxLimitAndPlusFilter ? Math.ceil(filter.min) : 0;
  const filterMax = pricingMaxLimitAndPlusFilter ?? Math.ceil(filter.max);
  // const initLocalRange = [filterMin, filterMax];
  const [localRange, setLocalRange] = useState<any>(
    sideFilters[filter.attr]
      ? [
          Math.ceil(sideFilters[filter.attr][0]),
          Math.ceil(sideFilters[filter.attr][1]),
        ]
      : [filterMin, filterMax]
  );

  // This change helps to update slider value according to search params
  useEffect(() => {
    if (!sideFilters[filter.attr]) {
      setLocalRange([filterMin, filterMax]);
    }
  }, [sideFilters, filter, filterMin, filterMax]);

  const handleChange = (e: any) => {
    setLocalRange(e);
  };


  // helper that updates either local filters (if provided) or redux
  const commitFilter = (key: string, value: any) => {
    if (setLocalSideFilters) {
      // update local copy immutably
      setLocalSideFilters({
        ...(localSideFilters || {}),
        [key]: value,
      });
      return;
    }
    const fn = withModalScopeParams ? updateModalSideFilters : updateSiteFilter;
    dispatch(fn({ key: filter.attr, value: value }));
  };


  const handleAfterChange = (newValue: any) => {
    commitFilter(filter.attr, newValue);
  };
  // const { general: t } = useSelector(
  //   (state: RootState) => state.config.translations
  // );

  const attributeTranslation = useGetAttributeTranslation();

  const [priceMin, priceMax, sliderMin, sliderMax] = useMemo(() => {
    const pMin = Math.ceil((localRange[0] + additionalPrice) / totalPax);
    const pMax =
      pricingMaxLimitAndPlusFilter &&
      localRange[1] > pricingMaxLimitAndPlusFilter - 1
        ? `${Math.ceil(
            (pricingMaxLimitAndPlusFilter + additionalPrice) / totalPax
          )}+`
        : Math.ceil((localRange[1] + additionalPrice) / totalPax);

    const min = filter.attr === 'distance' ? filter.min : filterMin;
    const max = filter.attr === 'distance' ? filter.max : filterMax;
    return [pMin, pMax, min, max];
  }, [
    localRange,
    additionalPrice,
    totalPax,
    pricingMaxLimitAndPlusFilter,
    filter.attr,
    filter.min,
    filter.max,
    filterMin,
    filterMax,
  ]);

  if ((!sliderMin && !sliderMax) || sliderMin === sliderMax) {
    return null;
  }

  if (isLoading) {
    return <Skeleton active paragraph={{ rows: 1 }} />;
  }

  return (
    <div className="numeric-range">
      <h2>{attributeTranslation(filter.attr)}</h2>

      <div className="unt">
        <span>
          {priceMin}
          {filter.unit}
        </span>
        <span>
          {priceMax}
          {filter.unit}
        </span>
      </div>
      <Slider
        range
        onChange={handleChange}
        onAfterChange={handleAfterChange}
        value={localRange}
        min={sliderMin}
        max={sliderMax}
        tooltip={{
          formatter: (num) => {
            if (!num) {
              return '0';
            }
            return pricingMaxLimitAndPlusFilter &&
              num > pricingMaxLimitAndPlusFilter - 1
              ? `${Math.ceil(
                  (pricingMaxLimitAndPlusFilter + additionalPrice) / totalPax
                )}+`
              : Math.ceil((num + additionalPrice) / totalPax);
          },
        }}
      />
    </div>
  );
};

export default SideFilterNumericRange;
